/**
 * Org chooser — lists orgs the signed-in user belongs to (`GET /api/v1/orgs`),
 * auto-enters when there is exactly one, and routes to the create-vs-join
 * onboarding funnel (`/orgs/choice`) for users who want a new workspace.
 * Search (`?q=`) lives in URL params per doctrine §1.8.
 */
import { useEffect } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Plus, SearchX } from 'lucide-react'
import { apiFetch } from '@/api/client'
import { useAuth } from '@/app/auth-context'
import type { Organization } from '@/domain'
import { Button, EmptyState, Input } from '@/components/ui'
import { ChooserFooter, ChooserHeader, LoadError, OrgListSkeleton, OrgRow } from './chooser-parts'

const ORG_KEY = 'it.orgId'
const SEARCH_VISIBLE_FROM = 6 // search affordance only once the list is long

export const OrgChooserPage = function OrgChooserPage() {
  const vm = useOrgChooser()
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--s-12) var(--s-6)',
        background: 'var(--bg)',
      }}
    >
      <main style={{ width: '100%', maxWidth: 560 }}>
        <ChooserHeader email={vm.email} count={vm.total} />

        {vm.orgs.length >= SEARCH_VISIBLE_FROM && <OrgSearch vm={vm} />}

        <div style={{ marginTop: 'var(--s-5)' }}>
          <OrgListStates vm={vm} />
        </div>

        <ChooserFooter
          disabled={vm.isPending || vm.isError}
          onCreate={() => void vm.goToOnboarding()}
        />
      </main>
    </div>
  )
}

/* ───────────────────────── view-model ───────────────────────── */

export type ChooserVm = {
  email?: string
  total?: number
  q: string
  orgs: Organization[]
  visible: Organization[]
  isPending: boolean
  isError: boolean
  refetch: () => void
  updateSearch: (patch: Record<string, unknown>, replace?: boolean) => void
  choose: (org: Organization) => void
  goToOnboarding: () => void
}

function useOrgChooser(): ChooserVm {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { session } = useAuth()
  const search = useSearch({ strict: false }) as Record<string, unknown>
  const q = typeof search.q === 'string' ? search.q : ''

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['orgs'],
    queryFn: ({ signal }) => apiFetch<Organization[]>({ url: '/api/v1/orgs', signal }),
  })

  function updateSearch(patch: Record<string, unknown>, replace = false) {
    void navigate({ to: '/orgs', search: cleanSearch({ ...search, ...patch }), replace })
  }

  function choose(org: Organization) {
    const previous = sessionStorage.getItem(ORG_KEY)
    sessionStorage.setItem(ORG_KEY, org.id)
    if (previous !== org.id) void queryClient.invalidateQueries()
    void navigate({ to: '/dashboard' })
  }

  function goToOnboarding() {
    void navigate({ to: '/orgs/choice' })
  }

  // Auto-enter when the user belongs to exactly one org (primary-org rule).
  useEffect(() => {
    if (!data || data.length !== 1) return
    sessionStorage.setItem(ORG_KEY, data[0].id)
    void navigate({ to: '/dashboard' })
  }, [data, navigate])

  const orgs = (data ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
  return {
    email: session?.email, total: data?.length, q,
    orgs, visible: filterOrgs(orgs, q),
    isPending, isError, refetch: () => void refetch(), updateSearch, choose, goToOnboarding,
  }
}

/** Drop empty/cleared params so the URL stays canonical and bookmarkable. */
function cleanSearch(next: Record<string, unknown>): Record<string, unknown> {
  const out = { ...next }
  for (const key of Object.keys(out)) {
    if (out[key] === undefined || out[key] === '' || out[key] === false) delete out[key]
  }
  return out
}

function filterOrgs(orgs: readonly Organization[], q: string): Organization[] {
  const needle = q.trim().toLowerCase()
  if (!needle) return [...orgs]
  return orgs.filter((o) => `${o.name} ${o.id}`.toLowerCase().includes(needle))
}

/* ───────────────────────── sections ───────────────────────── */

function OrgSearch({ vm }: { vm: ChooserVm }) {
  return (
    <div style={{ margin: 'var(--s-4) 0' }}>
      <Input
        label="Search organizations"
        icon="search"
        placeholder="Name or org ID…"
        value={vm.q}
        onChange={(e) => vm.updateSearch({ q: e.currentTarget.value }, true)}
      />
    </div>
  )
}

/** The org list's five states: skeleton / error / true-empty / filtered-empty / populated. */
function OrgListStates({ vm }: { vm: ChooserVm }) {
  if (vm.isPending) return <OrgListSkeleton />
  if (vm.isError) return <LoadError onRetry={vm.refetch} />
  if (vm.orgs.length === 0) {
    return (
      <EmptyState
        icon={Building2}
        title="No organizations yet"
        hint="Create your first organization to start connecting cloud environments, or ask an admin for an invite link to join an existing one."
        action={
          <Button leadingIcon={Plus} onClick={() => vm.goToOnboarding()}>
            Create organization
          </Button>
        }
      />
    )
  }
  if (vm.visible.length === 0) {
    return (
      <EmptyState
        icon={SearchX}
        title={`No organizations match “${vm.q}”`}
        hint="Check the spelling, or clear the search to see every organization."
        action={
          <Button variant="outline" onClick={() => vm.updateSearch({ q: undefined }, true)}>
            Clear search
          </Button>
        }
      />
    )
  }
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
      {vm.visible.map((org, i) => (
        <li key={org.id}>
          <OrgRow org={org} index={i} isNew={false} onChoose={vm.choose} />
        </li>
      ))}
    </ul>
  )
}
